import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { RootStatePlatform } from "../../store/platform-slice";
import { useAuth } from "../../../../providers/AuthProvider";
import { useSubject } from "../../providers/SubjectProvider";
import { useClientGender } from "../../hooks/useClientGender";
import { countPendingSystems } from "./pendingApprovals";
import InfoIcon from "../../svg/InfoIcon";

/**
 * Affiliate-only pending-approvals pill on the Health overview: shown while
 * any system score is pending approval after new biomarker data landed.
 * "Review" asks the screen to open the Review health scores modal (approve /
 * hide per system) — the screen owns that modal because the post-save snackbar
 * opens it too. Rendered inside the screen's floating toolbar slot, so it takes
 * no layout space — appearing never pushes the content or causes a page scroll.
 */
const PendingApprovalsBanner = ({ onReview }: { onReview: () => void }) => {
  const theme: Theme = useTheme();
  const { AuthUser } = useAuth();
  const { subjectId } = useSubject();

  const systems = useSelector(
    (state: RootStatePlatform) =>
      state.platform.dataPlatformServer.score?.systems
  );
  const isDemoReport = useSelector(
    (state: RootStatePlatform) => state.platform.dataPlatformServer.isDemo
  );
  const gender = useClientGender();

  const pendingCount = useMemo(
    () => countPendingSystems(systems, gender),
    [systems, gender]
  );

  // Customers never see the approvals flow. Sample (demo) reports show the
  // "Faded sections display sample data…" message instead — there is nothing
  // real to approve, so the banner stays hidden.
  if (
    AuthUser?.role === 1 ||
    isDemoReport ||
    pendingCount === 0 ||
    !subjectId
  )
    return null;

  return (
    <Box sx={styles.banner(theme)}>
      <Box sx={styles.message(theme)}>
        <InfoIcon color={theme.colorTokens.W[80]} size={14} />
        Pending biomarker approvals. Review new scores to update client view.
      </Box>
      <Box sx={styles.reviewLink(theme)} onClick={onReview}>
        Review
      </Box>
    </Box>
  );
};

export default PendingApprovalsBanner;

const styles = {
  banner: (theme: Theme) => ({
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    padding: "9px 16px",
    borderRadius: "8px",
    backgroundColor: theme.colorTokens.W[10],
    border: `1px solid ${theme.colorTokens.W[30]}`,
  }),
  message: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontFamily: "var(--font-Medium)",
    fontSize: "13px",
    lineHeight: "18px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    whiteSpace: "nowrap" as const,
  }),
  reviewLink: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "13px",
    lineHeight: "18px",
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    cursor: "pointer",
    whiteSpace: "nowrap" as const,
  }),
};
